'use client';
import React, { useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import { MapPin, Clock, Heart, Phone, MessageCircle, Share2, Flag, Eye, Shield, Mail, Copy, Check, ChevronLeft, Star } from 'lucide-react';

interface Listing {
  id: string;
  title: string;
  price: string;
  priceNote?: string;
  location: string;
  timeAgo: string;
  condition: string;
  conditionColor: string;
  category: string;
  image: string;
  alt: string;
  seller: string;
  sellerVerified: boolean;
  sellerLocation?: string;
  views: number;
  saved: number;
  phone?: string;
  phone2?: string;
  email?: string;
  rating?: number;
  reviews?: number;
  badge?: string;
  badgeColor?: string;
  description?: string;
}

export const allListings: Listing[] = [
  // Featured items
  {
    id: 'feat-001', title: 'Toyota Land Cruiser V8 2020', category: 'Vehicles',
    price: 'KES 8,500,000', location: 'Westlands, NBI', rating: 4.9, reviews: 24,
    timeAgo: '2 hrs ago', condition: 'Used', conditionColor: '#FFB300',
    image: "https://images.unsplash.com/photo-1677519498852-a86ba7aa747c",
    alt: 'White Toyota Land Cruiser V8 parked on a city street in Nairobi',
    badge: 'FEATURED', badgeColor: '#FFB300',
    seller: 'Amina W.', sellerVerified: true, sellerLocation: 'Westlands, Nairobi',
    views: 1240, saved: 89,
    phone: '+254712000001', phone2: '+254733000001', email: 'amina@example.com',
    description: 'Well-maintained Toyota Land Cruiser V8 2020 in excellent condition. Full service history, original paint, sunroof, leather seats, 7-seater. Low mileage. Serious buyers only.'
  },
  {
    id: 'feat-002', title: '3BR Apartment Karen Estate', category: 'Property',
    price: 'KES 75,000/mo', location: 'Karen, NBI', rating: 4.7, reviews: 12,
    timeAgo: '1 day ago', condition: 'Available', conditionColor: '#00C853',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b6189bd6-1781267536879.png",
    alt: 'Modern three-bedroom apartment with garden view in Karen Nairobi',
    badge: 'HOT', badgeColor: '#FF3B5C',
    seller: 'Habitat KE', sellerVerified: true, sellerLocation: 'Karen, Nairobi',
    views: 2100, saved: 340,
    phone: '+254712000003', email: 'habitat@example.com',
    description: 'Spacious 3-bedroom apartment in the heart of Karen Estate. Modern kitchen, 2 bathrooms, parking, 24hr security, backup generator. Available immediately.'
  },
  {
    id: 'feat-003', title: 'iPhone 15 Pro Max 256GB', category: 'Electronics',
    price: 'KES 165,000', location: 'CBD, NBI', rating: 4.8, reviews: 56,
    timeAgo: '5 hrs ago', condition: 'New', conditionColor: '#00C853',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a43fe270-1765259226080.png",
    alt: 'Black iPhone 15 Pro Max displayed on white background',
    badge: 'VERIFIED', badgeColor: '#00C853',
    seller: 'TechHub KE', sellerVerified: true, sellerLocation: 'CBD, Nairobi',
    views: 890, saved: 124,
    phone: '+254712000002', phone2: '+254722000002', email: 'techhub@example.com',
    description: 'Brand new iPhone 15 Pro Max 256GB Natural Titanium. Sealed box, comes with original accessories. 1-year Apple warranty. Receipt provided.'
  },
  {
    id: 'feat-004', title: 'Sarova Stanley Nairobi — Deluxe Room', category: 'Hotel',
    price: 'KES 12,500/night', location: 'CBD, NBI', rating: 4.6, reviews: 340,
    timeAgo: '3 hrs ago', condition: 'Available', conditionColor: '#00C853',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a6e22c9b-1773145655512.png",
    alt: 'Luxurious hotel room with king bed and city views at Sarova Stanley',
    badge: 'TOP RATED', badgeColor: '#7C4DFF',
    seller: 'Sarova Hotels', sellerVerified: true, sellerLocation: 'CBD, Nairobi',
    views: 5600, saved: 890,
    phone: '+254712000020', email: 'reservations@sarovahotels.com',
    description: 'Deluxe room at Sarova Stanley Nairobi. King bed, city views, complimentary breakfast, WiFi, pool access. Book now for best rates.'
  },
  {
    id: 'feat-005', title: 'Senior Software Engineer — Safaricom PLC', category: 'Jobs',
    price: 'KES 180,000/mo', location: 'Westlands, NBI', rating: 4.9, reviews: 8,
    timeAgo: '1 day ago', condition: 'Open', conditionColor: '#00C853',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_130d53363-1769451772821.png",
    alt: 'Modern open-plan tech office with collaborative workspaces',
    badge: 'URGENT', badgeColor: '#FF6D00',
    seller: 'Safaricom PLC', sellerVerified: true, sellerLocation: 'Westlands, Nairobi',
    views: 3400, saved: 560,
    phone: '+254722000050', email: 'careers@safaricom.co.ke',
    description: 'Safaricom PLC is hiring a Senior Software Engineer. 5+ years experience in Java/Kotlin or Node.js. Competitive salary, medical cover, pension. Apply with CV and cover letter.'
  },
  // Classifieds
  {
    id: 'lst-001', title: 'Toyota Land Cruiser V8 2020 Black', price: 'KES 8,500,000',
    location: 'Westlands', timeAgo: '2 hrs ago', condition: 'Used', conditionColor: '#FFB300',
    category: 'Cars', seller: 'Amina W.', sellerVerified: true, sellerLocation: 'Westlands, Nairobi',
    views: 1240, saved: 89,
    phone: '+254712000001', phone2: '+254733000001', email: 'amina@example.com',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe7a34a9-1784150009430.png",
    alt: 'Black Toyota Land Cruiser V8 parked on clean driveway',
    description: 'Well-maintained Toyota Land Cruiser V8 2020 Black. Full service history, original paint, sunroof, leather seats, 7-seater. Low mileage. Serious buyers only.'
  },
  {
    id: 'lst-002', title: 'iPhone 15 Pro Max 256GB Natural Titanium', price: 'KES 165,000',
    location: 'Kilimani', timeAgo: '5 hrs ago', condition: 'New', conditionColor: '#00C853',
    category: 'Phones', seller: 'TechHub KE', sellerVerified: true, sellerLocation: 'Kilimani, Nairobi',
    views: 890, saved: 124,
    phone: '+254712000002', phone2: '+254722000002', email: 'techhub@example.com',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17b82fb7a-1772960574407.png",
    alt: 'Natural titanium iPhone 15 Pro Max on white background',
    description: 'Brand new iPhone 15 Pro Max 256GB Natural Titanium. Sealed box, original accessories, 1-year Apple warranty. Receipt provided.'
  },
  {
    id: 'lst-003', title: '3-Bedroom Apartment — Karen Estate', price: 'KES 75,000/mo',
    location: 'Karen', timeAgo: '1 day ago', condition: 'Available', conditionColor: '#00C853',
    category: 'Property', seller: 'Habitat KE', sellerVerified: true, sellerLocation: 'Karen, Nairobi',
    views: 2100, saved: 340,
    phone: '+254712000003', email: 'habitat@example.com',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f989a253-1777738357152.png",
    alt: 'Modern three-bedroom apartment with garden in Karen Nairobi',
    description: 'Spacious 3-bedroom apartment in Karen Estate. Modern kitchen, 2 bathrooms, parking, 24hr security, backup generator. Available immediately.'
  },
  {
    id: 'lst-004', title: 'Yamaha FZ16 2022 Red Motorbike', price: 'KES 185,000',
    location: 'Eastleigh', timeAgo: '3 hrs ago', condition: 'Used', conditionColor: '#FFB300',
    category: 'Vehicles', seller: 'Moses K.', sellerVerified: false, sellerLocation: 'Eastleigh, Nairobi',
    views: 430, saved: 28,
    phone: '+254712000004',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ef9d53aa-1784454153020.png",
    alt: 'Red Yamaha FZ16 motorbike on a Nairobi street',
    description: 'Yamaha FZ16 2022 in good condition. Recently serviced, new tyres, logbook ready. Ideal for delivery or personal use.'
  },
  {
    id: 'lst-005', title: 'MacBook Pro M3 14-inch Space Grey', price: 'KES 210,000',
    location: 'Gigiri', timeAgo: '6 hrs ago', condition: 'New', conditionColor: '#00C853',
    category: 'Electronics', seller: 'iStore KE', sellerVerified: true, sellerLocation: 'Gigiri, Nairobi',
    views: 1560, saved: 210,
    phone: '+254712000005', phone2: '+254733000005', email: 'istore@example.com',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17d746289-1767469901251.png",
    alt: 'Space grey MacBook Pro M3 open on a wooden desk',
    description: 'Brand new MacBook Pro M3 14-inch Space Grey. 16GB RAM, 512GB SSD. Sealed box with full Apple warranty. Receipt provided.'
  },
  {
    id: 'lst-006', title: 'Leather Sofa Set 7-Seater Brown', price: 'KES 85,000',
    location: 'South B', timeAgo: '12 hrs ago', condition: 'Used', conditionColor: '#FFB300',
    category: 'Furniture', seller: 'Janet M.', sellerVerified: false, sellerLocation: 'South B, Nairobi',
    views: 320, saved: 45,
    phone: '+254712000006',
    image: "https://images.unsplash.com/photo-1645528528803-9ef5744f02e3",
    alt: 'Brown leather sofa set in a modern living room',
    description: 'Genuine leather sofa set, 7-seater L-shape. Slightly used, very good condition. Moving sale — must go. Delivery available within Nairobi.'
  },
  {
    id: 'lst-007', title: 'Friesian Dairy Cow — 15L/day', price: 'KES 95,000',
    location: 'Nakuru', timeAgo: '2 days ago', condition: 'Healthy', conditionColor: '#00C853',
    category: 'Agriculture', seller: 'FarmKe Ltd', sellerVerified: true, sellerLocation: 'Nakuru Town',
    views: 780, saved: 67,
    phone: '+254712000007', email: 'farmke@example.com',
    image: "https://images.unsplash.com/photo-1611087368593-5f77324c734c",
    alt: 'Black and white Friesian dairy cow in a green field',
    description: 'High-yield Friesian dairy cow producing 15 litres per day. Vaccinated, dewormed, healthy. Ideal for small-scale dairy farming.'
  },
  {
    id: 'lst-008', title: 'Samsung 65" 4K QLED Smart TV', price: 'KES 145,000',
    location: 'Lavington', timeAgo: '8 hrs ago', condition: 'New', conditionColor: '#00C853',
    category: 'Electronics', seller: 'Electronics Hub', sellerVerified: true, sellerLocation: 'Lavington, Nairobi',
    views: 560, saved: 90,
    phone: '+254712000008', phone2: '+254722000008',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bb34276d-1771891212623.png",
    alt: 'Large Samsung QLED 4K television mounted on wall in modern home',
    description: 'Samsung 65" 4K QLED Smart TV. HDR, Dolby Atmos, built-in apps. Brand new, sealed box. Free delivery within Nairobi.'
  },
  {
    id: 'lst-009', title: 'Ankara Print Dress Collection (5 pcs)', price: 'KES 8,500',
    location: 'Gikomba', timeAgo: '4 hrs ago', condition: 'New', conditionColor: '#00C853',
    category: 'Fashion', seller: 'Zawadi Styles', sellerVerified: false, sellerLocation: 'Gikomba Market, Nairobi',
    views: 1890, saved: 234,
    phone: '+254712000009', email: 'zawadi@example.com',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1be78ef93-1775337194400.png",
    alt: 'Colourful Ankara print dresses hanging on display rack',
    description: 'Beautiful Ankara print dress collection, 5 pieces in assorted sizes and patterns. Perfect for events, office, or casual wear. Wholesale and retail available.'
  },
  {
    id: 'lst-010', title: 'Nissan X-Trail 2019 Silver 4WD', price: 'KES 3,200,000',
    location: 'Parklands', timeAgo: '1 day ago', condition: 'Used', conditionColor: '#FFB300',
    category: 'Cars', seller: 'AutoWorld KE', sellerVerified: true, sellerLocation: 'Parklands, Nairobi',
    views: 2340, saved: 456,
    phone: '+254712000010', phone2: '+254733000010', email: 'autoworld@example.com',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_149d0cc56-1778237436945.png",
    alt: 'Silver Nissan X-Trail 4WD SUV parked in urban setting',
    description: 'Nissan X-Trail 2019 Silver 4WD. Low mileage, full service history, sunroof, leather seats, reverse camera. Accident-free. Logbook ready.'
  },
  {
    id: 'lst-011', title: 'Plot 0.5 Acre Thika Road — Title Deed', price: 'KES 4,800,000',
    location: 'Thika', timeAgo: '3 days ago', condition: 'Available', conditionColor: '#00C853',
    category: 'Property', seller: 'LandMark KE', sellerVerified: true, sellerLocation: 'Thika Town',
    views: 4500, saved: 890,
    phone: '+254712000011', email: 'landmark@example.com',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b3327534-1772466446472.png",
    alt: 'Empty land plot with clear boundary markers near Thika road',
    description: '0.5 acre plot on Thika Road with ready title deed. Ideal for residential or commercial development. Electricity and water available. Negotiable.'
  },
  {
    id: 'lst-012', title: 'Dell XPS 15 Intel i9 32GB RAM', price: 'KES 185,000',
    location: 'Upperhill', timeAgo: '9 hrs ago', condition: 'New', conditionColor: '#00C853',
    category: 'Electronics', seller: 'CompuTech KE', sellerVerified: true, sellerLocation: 'Upperhill, Nairobi',
    views: 670, saved: 78,
    phone: '+254712000012', phone2: '+254722000012',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b375b3cc-1773036210448.png",
    alt: 'Dell XPS 15 laptop open showing high-resolution display',
    description: 'Dell XPS 15 Intel i9, 32GB RAM, 1TB SSD, NVIDIA RTX 4060. Brand new, sealed. Perfect for developers, designers, and video editors.'
  },
  // Recent classifieds
  {
    id: 'cls-001', title: 'Samsung Galaxy S24 Ultra', price: 'KES 142,000',
    location: 'Kilimani', timeAgo: '5 min ago', condition: 'New', conditionColor: '#00C853',
    category: 'Phones', seller: 'TechHub KE', sellerVerified: true, sellerLocation: 'Kilimani, Nairobi',
    views: 340, saved: 56,
    phone: '+254712000002', email: 'techhub@example.com',
    image: "https://images.unsplash.com/photo-1707410420102-faff6eb0e033",
    alt: 'Black Samsung Galaxy S24 Ultra smartphone on white surface',
    description: 'Brand new Samsung Galaxy S24 Ultra. 256GB, Titanium Black. Sealed box with full Samsung warranty. Receipt provided.'
  },
  {
    id: 'cls-002', title: 'Leather Sofa Set 7-Seater', price: 'KES 85,000',
    location: 'South B', timeAgo: '12 min ago', condition: 'Used', conditionColor: '#FFB300',
    category: 'Furniture', seller: 'Janet M.', sellerVerified: false, sellerLocation: 'South B, Nairobi',
    views: 120, saved: 18,
    phone: '+254712000006',
    image: "https://images.unsplash.com/photo-1646462778394-32985c3d566f",
    alt: 'Brown leather sofa set arranged in a modern living room',
    description: 'Genuine leather sofa set, 7-seater. Slightly used, very good condition. Moving sale. Delivery available within Nairobi.'
  },
  {
    id: 'cls-003', title: 'Yamaha FZ16 2022 Motorbike', price: 'KES 185,000',
    location: 'Eastleigh', timeAgo: '25 min ago', condition: 'Used', conditionColor: '#FFB300',
    category: 'Vehicles', seller: 'Moses K.', sellerVerified: false, sellerLocation: 'Eastleigh, Nairobi',
    views: 89, saved: 12,
    phone: '+254712000004',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_107f44db4-1772638688721.png",
    alt: 'Blue Yamaha FZ16 motorbike parked on a street',
    description: 'Yamaha FZ16 2022 in good condition. Recently serviced, new tyres, logbook ready.'
  },
  {
    id: 'cls-004', title: 'MacBook Pro M3 14-inch', price: 'KES 210,000',
    location: 'Gigiri', timeAgo: '1 hr ago', condition: 'New', conditionColor: '#00C853',
    category: 'Electronics', seller: 'iStore KE', sellerVerified: true, sellerLocation: 'Gigiri, Nairobi',
    views: 230, saved: 45,
    phone: '+254712000005', phone2: '+254733000005', email: 'istore@example.com',
    image: "https://images.unsplash.com/photo-1515803171005-2d5e90f7b4d3",
    alt: 'Silver MacBook Pro laptop open on a desk with coffee',
    description: 'Brand new MacBook Pro M3 14-inch. 16GB RAM, 512GB SSD. Sealed box with full Apple warranty.'
  },
  {
    id: 'cls-005', title: 'Ankara Dress Collection (10 pcs)', price: 'KES 12,500',
    location: 'Gikomba', timeAgo: '2 hrs ago', condition: 'New', conditionColor: '#00C853',
    category: 'Fashion', seller: 'Zawadi Styles', sellerVerified: false, sellerLocation: 'Gikomba Market, Nairobi',
    views: 450, saved: 78,
    phone: '+254712000009', email: 'zawadi@example.com',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1158e7cf7-1775230476446.png",
    alt: 'Colorful Ankara print dresses hanging on rack in market',
    description: 'Beautiful Ankara print dress collection, 10 pieces in assorted sizes and patterns. Wholesale and retail available.'
  },
  {
    id: 'cls-006', title: 'Dairy Cow — Friesian Cross', price: 'KES 95,000',
    location: 'Nakuru', timeAgo: '3 hrs ago', condition: 'Healthy', conditionColor: '#00C853',
    category: 'Agriculture', seller: 'FarmKe Ltd', sellerVerified: true, sellerLocation: 'Nakuru Town',
    views: 190, saved: 34,
    phone: '+254712000007', email: 'farmke@example.com',
    image: "https://images.unsplash.com/photo-1549995177-8f92d78d1751",
    alt: 'Black and white Friesian dairy cow standing in a green field',
    description: 'High-yield Friesian cross dairy cow. Vaccinated, dewormed, healthy. Ideal for small-scale dairy farming.'
  },
];

function toSlug(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function categoryToPath(category: string): string {
  const map: Record<string, string> = {
    'Cars': 'cars', 'Vehicles': 'vehicles', 'Phones': 'phones',
    'Electronics': 'electronics', 'Fashion': 'fashion', 'Property': 'property',
    'Agriculture': 'agriculture', 'Furniture': 'furniture', 'Services': 'services',
  };
  return map[category] || category.toLowerCase();
}

export default function ProductDetailPage() {
  const params = useParams();
  const router = useRouter();
  const id = params?.id as string;

  const item = allListings.find((l) => l.id === id);

  const [isSaved, setIsSaved] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 2000); };

  if (!item) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4"
        style={{ background: 'var(--background)' }}>
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl"
          style={{ background: 'var(--secondary)', border: '1px solid var(--border)' }}>🔍</div>
        <p className="text-lg font-700 text-foreground">Product not found</p>
        <button onClick={() => router.back()}
          className="px-5 py-2.5 rounded-xl text-sm font-600"
          style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
          Go Back
        </button>
      </div>
    );
  }

  const productSlug = toSlug(item.title);
  const categoryPath = categoryToPath(item.category);
  const shareUrl = `https://chinandogo.com/${categoryPath}/${productSlug}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setLinkCopied(true);
    showToast('Product link copied!');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleWhatsAppShare = () => {
    const text = `Check out this listing on China Ndogo:\n*${item.title}* — ${item.price}\n📍 ${item.location}\n\n${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(`${item.title} — ${item.price} on China Ndogo`);
    const body = encodeURIComponent(`Hi,\n\nI found this listing on China Ndogo:\n\n${item.title}\nPrice: ${item.price}\nLocation: ${item.location}\n\nView it here: ${shareUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-xl text-sm font-semibold text-white"
          style={{ background: 'var(--primary)' }}>{toast}</div>
      )}

      {/* Hero Image */}
      <div className="relative w-full" style={{ maxHeight: '340px', overflow: 'hidden' }}>
        <AppImage src={item.image} alt={item.alt} width={800} height={340}
          className="w-full object-cover" style={{ maxHeight: '340px' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 40%, rgba(0,0,0,0.5) 100%)' }} />

        {/* Back button */}
        <button onClick={() => router.back()}
          className="absolute top-4 left-4 w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}>
          <ChevronLeft size={20} color="#fff" />
        </button>

        {/* Save button */}
        <button onClick={() => setIsSaved(!isSaved)}
          className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}>
          <Heart size={18} fill={isSaved ? '#FF4081' : 'none'} style={{ color: isSaved ? '#FF4081' : '#fff' }} />
        </button>

        {/* Condition badge */}
        <span className="absolute bottom-4 left-4 px-2.5 py-1 rounded-full text-xs font-700"
          style={{ background: item.conditionColor, color: '#000' }}>
          {item.condition}
        </span>

        {item.badge && (
          <span className="absolute bottom-4 right-4 px-2.5 py-1 rounded-full text-xs font-700"
            style={{ background: item.badgeColor, color: '#000' }}>
            {item.badge}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-5 pb-24">

        {/* Title & Price */}
        <div className="mb-4">
          <h1 className="text-xl font-800 text-foreground leading-tight mb-1">{item.title}</h1>
          <p className="text-2xl font-900 font-tabular" style={{ color: 'var(--primary)' }}>{item.price}</p>
        </div>

        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-3 mb-5 pb-4" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="flex items-center gap-1.5">
            <MapPin size={13} style={{ color: 'var(--muted-foreground)' }} />
            <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{item.location}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock size={13} style={{ color: 'var(--muted-foreground)' }} />
            <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{item.timeAgo}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Eye size={13} style={{ color: 'var(--muted-foreground)' }} />
            <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{item.views} views</span>
          </div>
          {item.rating && (
            <div className="flex items-center gap-1.5">
              <Star size={13} fill="var(--accent)" style={{ color: 'var(--accent)' }} />
              <span className="text-sm font-600 text-foreground">{item.rating}</span>
              {item.reviews && <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>({item.reviews})</span>}
            </div>
          )}
        </div>

        {/* Description */}
        {item.description && (
          <div className="mb-5">
            <h2 className="text-sm font-700 mb-2 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>Description</h2>
            <p className="text-sm leading-relaxed text-foreground">{item.description}</p>
          </div>
        )}

        {/* Seller Card */}
        <div className="flex items-center gap-3 p-4 rounded-2xl mb-5"
          style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-base font-800 flex-shrink-0"
            style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
            {item.seller.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-sm font-700 text-foreground">{item.seller}</span>
              {item.sellerVerified && (
                <>
                  <Shield size={13} style={{ color: '#00C853' }} />
                  <span className="text-[10px] font-600" style={{ color: '#00C853' }}>Verified</span>
                </>
              )}
            </div>
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{item.category} Seller</span>
            {item.sellerLocation && (
              <div className="flex items-center gap-1 mt-0.5">
                <MapPin size={11} style={{ color: '#00BCD4' }} />
                <span className="text-xs font-600" style={{ color: '#00BCD4' }}>{item.sellerLocation}</span>
              </div>
            )}
          </div>
        </div>

        {/* Contact Buttons */}
        {(item.phone || item.phone2 || item.email) && (
          <div className="mb-5">
            <h2 className="text-sm font-700 mb-3 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>Contact Seller</h2>
            <div className="flex flex-col gap-2">
              {item.phone && (
                <div className="grid grid-cols-2 gap-2">
                  <a href={`tel:${item.phone}`}
                    className="flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-700 transition-all active:scale-95"
                    style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                    <Phone size={16} /> Call
                  </a>
                  <a href={`https://wa.me/${item.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-700 transition-all active:scale-95"
                    style={{ background: 'rgba(37,211,102,0.1)', color: '#25D366', border: '1px solid rgba(37,211,102,0.25)' }}>
                    <MessageCircle size={16} /> WhatsApp
                  </a>
                </div>
              )}
              {item.phone2 && (
                <div className="grid grid-cols-2 gap-2">
                  <a href={`tel:${item.phone2}`}
                    className="flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-600 transition-all active:scale-95"
                    style={{ background: 'var(--secondary)', color: 'var(--foreground)', border: '1px solid var(--border)' }}>
                    <Phone size={14} /> {item.phone2}
                  </a>
                  <a href={`https://wa.me/${item.phone2.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-600 transition-all active:scale-95"
                    style={{ background: 'rgba(37,211,102,0.06)', color: '#25D366', border: '1px solid rgba(37,211,102,0.15)' }}>
                    <MessageCircle size={14} /> WhatsApp 2
                  </a>
                </div>
              )}
              {item.email && (
                <a href={`mailto:${item.email}?subject=Inquiry: ${encodeURIComponent(item.title)}`}
                  className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-600 transition-all active:scale-95"
                  style={{ background: 'rgba(234,67,53,0.08)', color: '#EA4335', border: '1px solid rgba(234,67,53,0.2)' }}>
                  <Mail size={16} /> Email Seller
                </a>
              )}
            </div>
          </div>
        )}

        {/* Share & Report */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button onClick={() => setShowShare(!showShare)}
            className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-600 transition-all active:scale-95"
            style={{ background: 'var(--secondary)', color: 'var(--foreground)', border: '1px solid var(--border)' }}>
            <Share2 size={15} /> Share
          </button>
          <button className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-600"
            style={{ background: 'rgba(255,59,92,0.06)', color: '#FF3B5C', border: '1px solid rgba(255,59,92,0.15)' }}>
            <Flag size={15} /> Report Ad
          </button>
        </div>

        {/* Share Panel */}
        {showShare && (
          <div className="p-4 rounded-2xl mb-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <p className="text-xs font-700 mb-2 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>Share This Listing</p>
            <div className="flex items-center gap-2 p-2.5 rounded-xl mb-3"
              style={{ background: 'var(--input)', border: '1px solid var(--border)' }}>
              <span className="text-[10px] flex-1 truncate font-mono" style={{ color: 'var(--primary)' }}>{shareUrl}</span>
              <button onClick={handleCopyLink}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-700 flex-shrink-0"
                style={{ background: linkCopied ? 'var(--primary)' : 'var(--card)', color: linkCopied ? '#fff' : 'var(--foreground)' }}>
                {linkCopied ? <Check size={10} /> : <Copy size={10} />}
                {linkCopied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div className="flex gap-3">
              <button onClick={handleWhatsAppShare}
                className="flex flex-col items-center gap-1 flex-1 py-2.5 rounded-xl transition-all active:scale-95"
                style={{ background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.2)' }}>
                <span className="text-xl">💬</span>
                <span className="text-[9px] font-600" style={{ color: '#25D366' }}>WhatsApp</span>
              </button>
              <button onClick={handleEmailShare}
                className="flex flex-col items-center gap-1 flex-1 py-2.5 rounded-xl transition-all active:scale-95"
                style={{ background: 'rgba(234,67,53,0.08)', border: '1px solid rgba(234,67,53,0.15)' }}>
                <span className="text-xl">📧</span>
                <span className="text-[9px] font-600" style={{ color: '#EA4335' }}>Email</span>
              </button>
              <button onClick={handleCopyLink}
                className="flex flex-col items-center gap-1 flex-1 py-2.5 rounded-xl transition-all active:scale-95"
                style={{ background: 'var(--secondary)', border: '1px solid var(--border)' }}>
                <span className="text-xl">🔗</span>
                <span className="text-[9px] font-600" style={{ color: 'var(--muted-foreground)' }}>Copy Link</span>
              </button>
            </div>
          </div>
        )}

        <p className="text-[10px] text-center" style={{ color: 'var(--muted-foreground)' }}>
          Always meet in a safe public place. Never send money before seeing the item.
        </p>
      </div>
    </div>
  );
}
